import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ComparisonPoint } from '@/hooks/useSuperStats';

interface VisitsChartProps {
  data: ComparisonPoint[];
  total: number;
  previousTotal: number;
  loading: boolean;
}

/**
 * Évolution des visites en aire, superposée avec la période précédente en
 * ligne pointillée — lecture directe de la traction.
 */
export default function VisitsChart({ data, total, previousTotal, loading }: VisitsChartProps) {
  const delta = previousTotal > 0 ? ((total - previousTotal) / previousTotal) * 100 : null;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 h-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
            <i className="ri-line-chart-line text-primary-500"></i>
            Évolution des visites — actuel vs période précédente
          </h3>
          <p className="text-xs text-foreground-400 mt-0.5">
            Accueil Zifek + sites + produits + services
          </p>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold font-heading text-foreground-950">
            {total.toLocaleString('fr-FR')}
          </div>
          <div className="text-[11px] text-foreground-400">
            visites sur la période
            {delta != null && (
              <span className={delta >= 0 ? ' text-accent-600' : ' text-red-500'}>
                {' '}{delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : data.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-foreground-400">
          <i className="ri-line-chart-line text-3xl mb-2"></i>
          <p className="text-sm">Aucune visite sur cette période</p>
        </div>
      ) : (
        <div className="h-64 md:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 5, right: 10, left: -12, bottom: 5 }}>
              <defs>
                <linearGradient id="visitsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="oklch(var(--primary-500))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="oklch(var(--primary-500))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--foreground-200) / 0.4)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'oklch(var(--foreground-500))' }}
                tickLine={false}
                axisLine={{ stroke: 'oklch(var(--foreground-200) / 0.4)' }}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={44}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'oklch(var(--background-50))',
                  border: '1px solid oklch(var(--foreground-200) / 0.5)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'oklch(var(--foreground-900))',
                }}
                labelStyle={{ color: 'oklch(var(--foreground-500))' }}
              />
              <Legend
                wrapperStyle={{ fontSize: '11px' }}
                formatter={(value) => (
                  <span style={{ color: 'oklch(var(--foreground-600))' }}>{value}</span>
                )}
              />
              <Area
                type="monotone"
                dataKey="current"
                name="Période actuelle"
                stroke="oklch(var(--primary-500))"
                strokeWidth={2}
                fill="url(#visitsFill)"
                activeDot={{ r: 4, fill: 'oklch(var(--primary-500))', strokeWidth: 0 }}
              />
              <Line
                type="monotone"
                dataKey="previous"
                name="Période précédente"
                stroke="oklch(var(--foreground-300))"
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}